import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import { FormHelperText, Grid } from '@mui/material';
import { DeleteOutlined, KeyboardArrowUp, KeyboardArrowDown, Add } from '@mui/icons-material';
import { withRights } from '../../../../../providers/RightsConsumer';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Autocomplete, Dialog, IconButton, TextField } from '@totalsoft/rocket-ui';
import {
  AddSigneeButton,
  ArrowButtonsContainer,
  BackdropCustom,
  DialogButton,
  DialogTitle,
  SecondDialogButton,
  SigneeCard
} from './DocumentsStyles';

const DocumentSigneesComponent = ({
  signees,
  swapUp,
  swapDown,
  onDelete,
  validation,
  addSignee,
  isSaving,
  isDirty,
  onPropertyChange,
  updateDocSignees,
  persons,
  entities,
  signatureRoles,
  partnerPositions,
  rights,
  open,
  onCloseModal
}) => {
  const { t } = useTranslation();

  const handleSwapUp = index => () => swapUp(index);
  const handleSwapDown = index => () => swapDown(index);
  const handleDelete = index => () => onDelete(index);

  return (
    <Dialog
      id='doc-signees-dialog'
      open={open}
      onClose={onCloseModal}
      fullWidth={true}
      maxWidth={'lg'}
      BackdropComponent={BackdropCustom}
      transitionDuration={0}
      scroll='body'
      showX={false}
      disableBackdropClick
      disableEscapeKeyDown
      actionsProps={{ sx: theme => ({ px: theme.spacing(3), pb: theme.spacing(2) }) }}
      title={<DialogTitle>{t('OfferEdit.Documents.Signees.Title')}</DialogTitle>}
      content={
        <Grid container>
          <Grid item xs={12}>
            <AddSigneeButton
              color='primary'
              variant='outlined'
              startIcon={<Add />}
              onClick={addSignee}
              disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
              isLargeButton={!signees.length}
            >
              {t('OfferEdit.Documents.Signees.AddSignee')}
            </AddSigneeButton>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              {signees?.map((signee, index) => {
                const person = persons[signee.partnerId]?.values?.find(doc => doc.personId === signee.personId);
                return (
                  <Grid item key={index} xs={12}>
                    <SigneeCard container>
                      <ArrowButtonsContainer item xs={12} sm='auto'>
                        <div>
                          <IconButton
                            id={'swapUp_' + index}
                            variant='text'
                            onClick={handleSwapUp(index)}
                            disabled={index === 0 || !rights.documentSigneesWrite || rights.readOnlyFull}
                          >
                            <KeyboardArrowUp />
                          </IconButton>
                        </div>
                        <div>
                          <IconButton
                            id={'swapDown_' + index}
                            variant='text'
                            onClick={handleSwapDown(index)}
                            disabled={index === signees.length - 1 || !rights.documentSigneesWrite || rights.readOnlyFull}
                          >
                            <KeyboardArrowDown />
                          </IconButton>
                        </div>
                      </ArrowButtonsContainer>
                      <Grid item xs={12} sm px={4}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Tooltip title={entities?.values?.find(e => e.partnerId === signee.partnerId)?.partnerName || ''}>
                              {
                                <Autocomplete
                                  id={'entity_' + index}
                                  fullWidth
                                  value={signee.partnerId ? signee.partnerId : ''}
                                  valueKey='partnerId'
                                  labelKey='partnerName'
                                  label={t('OfferEdit.Documents.Signees.Entity')}
                                  simpleValue={true}
                                  options={entities ? entities.values : []}
                                  error={!isValid(validation[index].partnerId)}
                                  helperText={getErrors(validation[index].partnerId)}
                                  onChange={onPropertyChange(index, 'partnerId')}
                                  disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
                                />
                              }
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Tooltip title={person?.personName ?? ''}>
                              {
                                <Autocomplete
                                  id={'person_' + index}
                                  fullWidth
                                  value={signee.personId ? signee.personId : ''}
                                  valueKey='personId'
                                  label={t('OfferEdit.Documents.Signees.Person')}
                                  labelKey='personName'
                                  simpleValue={true}
                                  error={!isValid(validation[index].personId)}
                                  helperText={getErrors(validation[index].personId)}
                                  onChange={onPropertyChange(index, 'personId')}
                                  options={signee.partnerId ? persons[signee.partnerId]?.values : []}
                                  disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
                                />
                              }
                            </Tooltip>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            {entities?.values?.find(e => e.partnerId === signee.partnerId)?.positionIsManual === true ? (
                              <Autocomplete
                                id={'position_' + index}
                                fullWidth
                                label={t('OfferEdit.Documents.Signees.Position')}
                                value={signee.positionId}
                                valueKey='positionId'
                                labelKey='positionName'
                                simpleValue={true}
                                options={partnerPositions}
                                onChange={onPropertyChange(index, 'positionId')}
                                disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
                              />
                            ) : (
                              <TextField
                                id={'position_' + index}
                                disabled={true}
                                fullWidth
                                label={t('OfferEdit.Documents.Signees.Position')}
                                value={person?.positionName ?? ''}
                              />
                            )}
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              id={'role_' + index}
                              disabled={true}
                              fullWidth
                              label={t('OfferEdit.Documents.Signees.Role')}
                              value={person?.partnerRoleName ?? ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Autocomplete
                              id={'role_' + index}
                              fullWidth
                              value={signee.signatureRoleId}
                              valueKey='dictionaryItemId'
                              labelKey='name'
                              label={t('OfferEdit.Documents.Signees.SignatureRole')}
                              simpleValue={true}
                              error={!isValid(validation[index].signatureRoleId)}
                              helperText={getErrors(validation[index].signatureRoleId)}
                              options={signatureRoles}
                              onChange={onPropertyChange(index, 'signatureRoleId')}
                              disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              id={'email_' + index}
                              disabled={true}
                              fullWidth
                              label={t('OfferEdit.Documents.Signees.Email')}
                              value={person?.email ?? ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              id={'phone_' + index}
                              disabled={true}
                              fullWidth
                              label={t('OfferEdit.Documents.Signees.Phone')}
                              value={person?.phone ?? ''}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12} sm='auto' display='flex' justifyContent='center'>
                        <IconButton
                          variant='text'
                          onClick={handleDelete(index)}
                          disabled={!rights.documentSigneesWrite || rights.readOnlyFull}
                          tooltip={t('Delete')}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Grid>
                    </SigneeCard>
                  </Grid>
                );
              })}
              {!isValid(validation) && signees && signees.length !== 0 && signees.every(x => x.personId) && (
                <Grid item>
                  <FormHelperText error={true} id='name-error-text'>
                    {getErrors(validation)}
                  </FormHelperText>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      }
      actions={
        <>
          <DialogButton color='primary' variant='outlined' onClick={onCloseModal} disabled={isSaving}>
            {isDirty ? t('Cancel') : t('Close')}
          </DialogButton>
          <SecondDialogButton
            color='primary'
            variant='contained'
            onClick={updateDocSignees}
            disabled={isSaving || !isDirty || !rights.documentSigneesWrite || rights.readOnlyFull}
          >
            {isSaving ? t('Saving') : t('Save')}
          </SecondDialogButton>
        </>
      }
    />
  );
};

DocumentSigneesComponent.propTypes = {
  signees: PropTypes.array.isRequired,
  swapUp: PropTypes.func.isRequired,
  swapDown: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired,
  addSignee: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  updateDocSignees: PropTypes.func.isRequired,
  signatureRoles: PropTypes.array.isRequired,
  partnerPositions: PropTypes.array.isRequired,
  persons: PropTypes.array.isRequired,
  entities: PropTypes.object.isRequired,
  rights: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired
};

export default withRights(DocumentSigneesComponent);
